// SmartPageLivePreviewPane — pane mounted by PrototypeTab when the
// user opens the Live Preview tab for a smart page (obj_type === 'S').
//
// Loads the page template, then branches:
//   - kind === 'document': render the DocPicker (list / new / delete);
//     selecting a doc switches to <SmartPageRuntime mode='document'>.
//   - kind === 'internal' | 'external': render a single
//     <SmartPageRuntime mode='preview'> with no doc CRUD.
//
// Mirrors the lazy-mount pattern used by LivePreviewLazyPane so the
// page-object fetch only runs when the tab is opened.

import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { ThemedPageShell } from '@twasta/page-runtime'
import { SiteLinkContext, isInternalHref } from '@twasta/page-runtime/renderers'
import { loadPageObject } from '@/api/designers'
import { toV2, normalizePageKind } from '@/pages/designers/page/migrate'
import { resolveTheme } from '@/pages/designers/page/theme'
import type { PageModel } from '@/pages/designers/page/types'
import { updateDocument, type DocumentSummary } from '@/api/documents'
import SmartPageRuntime from './SmartPageRuntime'
import DocPicker from './DocPicker'

interface Props {
  project: string
  objName: string
  /** Where the website this page belongs to lives, when it belongs to one —
   *  the `/site/{owner}/{project}/{site}` viewer, or an absolute address on a
   *  project's own domain (see `siteAddress`). A site page's own links are
   *  written in the SITE's space (`/blog/<slug>/`, `/contact`) — they mean
   *  something only under the site's base, and outside it they land on the
   *  platform's root, where no route answers. Only the site viewer can render
   *  a collection record's page, so that is where they go. */
  siteBase?: string
}

export default function SmartPageLivePreviewPane({ project, objName, siteBase }: Props) {
  const [page, setPage] = useState<PageModel | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [openDoc, setOpenDoc] = useState<DocumentSummary | null>(null)
  // Before-load gate decision (denied / redirect) surfaced as an informational
  // panel — in the designer we show what the gate WOULD do rather than yanking
  // the author out of the workbench (esp. for an external redirect).
  const [gateNotice, setGateNotice] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setPage(null)
    setLoadError(null)
    setOpenDoc(null)
    setGateNotice(null)
    // run=true → the server executes the page's before-load access event and
    // applies its decision (this is a runtime preview, not the editing canvas).
    loadPageObject(project, objName, true).then(
      (data) => {
        if (cancelled) return
        // Backend returns { content: PageRoot } or { page: ... }; normalize.
        const root = (data?.content ?? data) as { page?: PageModel } | PageModel
        const raw: PageModel = (root && 'page' in root && root.page ? root.page : root) as PageModel
        if (!raw || typeof raw !== 'object') {
          setLoadError('Page object did not include a page model.')
          return
        }
        const directive = (raw as { __before_load__?: {
          denied?: boolean
          redirect?: { kind: string; url?: string; page?: string }
          message?: string
        } }).__before_load__
        if (directive && (directive.denied || directive.redirect)) {
          const r = directive.redirect
          if (r?.kind === 'external' && r.url) {
            setGateNotice(`Before-load gate: would redirect to external URL → ${r.url}`)
          } else if (r?.kind === 'page' && r.page) {
            setGateNotice(`Before-load gate: would redirect to page "${r.page}".`)
          } else {
            setGateNotice(directive.message || 'Before-load gate: access denied.')
          }
          return
        }
        const normalized = normalizePageKind(toV2(raw))
        setPage(normalized)
      },
      (err: unknown) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        setLoadError(msg || 'Failed to load page')
      },
    )
    return () => { cancelled = true }
  }, [project, objName])

  // Resolve the page theme so the preview emits the author's `--pg-*` palette
  // (button colors, surfaces, text) — without this the runtime renders with
  // unthemed CSS-var fallbacks (e.g. white button text on a light page).
  const theme = useMemo(() => resolveTheme(page?.theme), [page?.theme])

  const siteLink = useMemo(() => ({
    resolveHref: (h: string) => {
      if (!siteBase || !isInternalHref(h)) return h
      if (h === siteBase || h.startsWith(`${siteBase}/`)) return h
      return h === '/' ? siteBase : `${siteBase}${h}`
    },
  }), [siteBase])

  if (loadError) {
    return (
      <div style={{
        padding: 16,
        border: '1px solid var(--color-error, #dc2626)',
        borderRadius: 6,
        color: 'var(--color-error, #dc2626)',
        fontSize: 14,
      }}>
        {loadError}
      </div>
    )
  }

  if (gateNotice) {
    return (
      <div style={{
        padding: 16,
        border: '1px solid var(--color-warning, #d97706)',
        borderRadius: 6,
        color: 'var(--color-warning, #b45309)',
        background: 'var(--color-warning-bg, rgba(217,119,6,0.08))',
        fontSize: 14,
      }}>
        <strong>Access gated</strong>
        <div style={{ marginTop: 6 }}>{gateNotice}</div>
        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
          The page's before-load event ran with the current URL parameters. In the
          deployed app the visitor is redirected / denied; here we show the decision
          so you can verify the gate without leaving the designer.
        </div>
      </div>
    )
  }

  if (!page) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        gap: 12,
        color: 'var(--color-text-secondary)',
        fontSize: 14,
      }}>
        <Loader2 size={16} className="spin" />
        Loading page…
      </div>
    )
  }

  const isDocumentKind = page.kind === 'document'

  // Document mode with a selected doc: editor view with a back link.
  if (isDocumentKind && openDoc) {
    return (
      // flexShrink: 0 — see the internal-page branch below: the workbench
      // mounts this in a flex-column container that would otherwise clamp the
      // shell to one viewport, bleeding the container bg through mid-page.
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => setOpenDoc(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              fontSize: 13,
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            <ArrowLeft size={12} />
            All documents
          </button>
          <EditableDocTitle
            doc={openDoc}
            onUpdated={(updated) => setOpenDoc(updated)}
          />
        </div>
        {/* constrain ON (default): cap content at the theme's content-width +
            side padding and center it, so the page background frames the
            content with margins — exactly what the design canvas does (it uses
            the same `layout.maxWidth ?? CONTENT_WIDTH_PX[contentWidth]`) and
            what the deployed runtime (PageRenderer) ships. With constrain off
            the content went edge-to-edge and lost those margins. */}
        <ThemedPageShell theme={theme} minHeight="100%" style={{ flexShrink: 0 }}>
          <SmartPageRuntime
            page={page}
            mode="document"
            docId={openDoc.id}
            project={project}
          />
        </ThemedPageShell>
      </div>
    )
  }

  // Document mode, no doc selected: show the picker.
  if (isDocumentKind) {
    return (
      <DocPicker
        project={project}
        pageObjName={objName}
        pageTitle={page.title || objName}
        onOpen={(doc) => setOpenDoc(doc)}
      />
    )
  }

  // Internal / external page: stateless preview. constrain ON (default) so the
  // content caps at the theme's content-width + padding and centers, framed by
  // the page background — matching the design canvas and the deployed runtime
  // (this same pane also backs the in-app /run view).
  //
  // flexShrink: 0 — the workbench mounts this pane inside a flex-column
  // container (PrototypeTab's content panel), where the shell would otherwise
  // be shrunk to one viewport height by the default flex-shrink + minHeight
  // 100%, leaving content below the fold with no page background behind it (the
  // workbench container's own bg then bled through mid-page). Pinning shrink to
  // 0 lets the shell grow to its full content height — matching the /run view,
  // whose scroll container is a plain block, so the shell already grows there.
  return (
    <SiteLinkContext.Provider value={siteLink}>
      <ThemedPageShell theme={theme} minHeight="100%" style={{ flexShrink: 0 }}>
        <SmartPageRuntime
          page={page}
          mode="preview"
          docId={null}
          project={project}
        />
      </ThemedPageShell>
    </SiteLinkContext.Provider>
  )
}

// Inline-editable document title. Click to edit, blur or Enter to save.
// Persists via PATCH /api/documents/{id} and bubbles the updated summary
// back so the parent state stays in sync.
function EditableDocTitle({
  doc, onUpdated,
}: {
  doc: DocumentSummary
  onUpdated: (next: DocumentSummary) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(doc.title || '')
  const [saving, setSaving] = useState(false)

  // Reset the draft whenever the upstream doc changes (e.g. after a
  // save, or when the user navigates between documents).
  useEffect(() => {
    setDraft(doc.title || '')
  }, [doc.title, doc.id])

  const commit = async () => {
    const trimmed = draft.trim()
    if (!trimmed || trimmed === doc.title) {
      setEditing(false)
      setDraft(doc.title || '')
      return
    }
    setSaving(true)
    try {
      const next = await updateDocument(doc.id, { title: trimmed })
      onUpdated(next)
      setEditing(false)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(`Failed to update title: ${msg}`)
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); void commit() }
          if (e.key === 'Escape') { setEditing(false); setDraft(doc.title || '') }
        }}
        style={{
          flex: 1,
          fontSize: 14, fontWeight: 500,
          color: 'var(--color-text)',
          background: 'var(--lp-input-bg, var(--color-bg))',
          border: '1px solid var(--color-border)',
          borderRadius: 4, padding: '3px 8px',
          outline: 'none',
        }}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Click to rename"
      style={{
        flex: 1, textAlign: 'left',
        fontSize: 14, fontWeight: 500,
        color: 'var(--color-text)',
        background: 'transparent', border: '1px solid transparent',
        borderRadius: 4, padding: '3px 8px',
        cursor: 'text',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border)' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent' }}
    >
      {doc.title || '(untitled)'}
    </button>
  )
}
